import PropTypes from 'prop-types';
import { API, Auth } from "aws-amplify";
import { getAuthToken } from '../../utils/AuthUtils';
import React, { useState } from 'react';
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { BILLABLE_TYPES, HUMAN_READABLE_BILLABLE_TYPES } from '../../Constants';
import ClientMetadataLookupInput from '../../CommonComponents/ClientMetadataLookupInputComponent/ClientMetadataLookupInput';
import UserLookupInput from '../../CommonComponents/UserLookupInputComponent/UserLookupInput';

import './styles.scss';

const DashboardDownloadConsole = ({ show, onClose, handleDownloadData, templateName, templateId }) => {
    const [billableType, setBillableType] = useState('');
    const [isBillableTypeSelected, setIsBillableTypeSelected] = useState(true)
    const [requiredNonBillable, setRequiredNonBillable] = useState(false);

    const [userNotes, setUserNotes] = useState('');
    const [characterLimit, setCharacterLimit] = useState(250);

    const [downloading, setDownloading] = useState(false);

    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [clientOwnerNameLookupTerm, setClientOwnerNameLookupTerm] = useState("");
    const [selectedClientOwnerUserRecord, setSelectedClientOwnerUserRecord] = useState({});
    const [clientOwnerNameValid, setClientOwnerNameValid] = useState(true);
    const [clientOwnerNameErrorMessage, setClientOwnerNameErrorMessage] = useState("");

    const [clientName, setClientName] = useState("");
    const [nsId, setNsId] = useState("");
    const [clientNameInvalid, setClientNameInvalid] = useState(false);
    const [isSubscriber, setIsSubscriber] = useState(false);

    const resetState = () => {
        setIsBillableTypeSelected(true);
        setClientNameInvalid(false);
        setRequiredNonBillable(false);
        setClientOwnerNameValid(true);
        setClientOwnerNameErrorMessage("");
        setShowError(false);
        setErrorMessage("");
    }

    const handleSubmit = async (e) => {
        resetState();

        if (billableType === "") {
            setIsBillableTypeSelected(false)
            return;
        } else {
            setIsBillableTypeSelected(true);
        }

        if (billableType === BILLABLE_TYPES.billable || billableType === BILLABLE_TYPES.customBillable) {
            if ((!clientName || clientName.length < 2 || clientName.trim() === "")) {
                setClientNameInvalid(true)
                return;
            } else {
                setClientNameInvalid(false);
            }

            if (clientOwnerNameLookupTerm === "" || clientOwnerNameLookupTerm === undefined || clientOwnerNameLookupTerm.trim() === ""
                || clientOwnerNameLookupTerm === null || clientOwnerNameLookupTerm.trim().split(" ").length < 2) {
                setClientOwnerNameErrorMessage('Client Owner Name should be in the format "First Last"');
                setClientOwnerNameValid(false);
                return;
            }

            if (selectedClientOwnerUserRecord === undefined || selectedClientOwnerUserRecord === null
                || Object.keys(selectedClientOwnerUserRecord).length === 0) {
                setErrorMessage("Please select a client owner");
                setShowError(true);
                return;
            }
        }

        if (userNotes.length === 0 && billableType !== BILLABLE_TYPES.billable && billableType !== BILLABLE_TYPES.customBillable) {
            setRequiredNonBillable(true);
            return;
        }

        setIsBillableTypeSelected(true);
        setClientOwnerNameValid(true);
        setClientNameInvalid(false);
        setRequiredNonBillable(false);

        await handleCreateDownloadRecord(billableType, userNotes);
    }


    const handleError = (errorMessage) => {
        setDownloading(false);
        setShowError(true);
        setErrorMessage(errorMessage);
    };

    const handleCreateDownloadRecord = async (billableType, userNotes) => {
        setErrorMessage(null);
        setShowError(false);
        setDownloading(true);

        const user = await Auth.currentAuthenticatedUser();

        const userId = user.signInUserSession.accessToken.payload.username;
        const userEmail = user.signInUserSession.accessToken.payload.useremail;
        const fullDateAccessed = new Date().toISOString();

        const requestBody = {
            userId,
            userEmail,
            templateId,
            templateName,
            billableType,
            clientName,
            nsId,
            isSubscriber,
            negotiatedRate: 1000,
            userNotes,
            adminNotes: "Dashboard data download",
            fullDateAccessed,
            clientOwnerName: selectedClientOwnerUserRecord.userName,
            clientOwnerUserId: selectedClientOwnerUserRecord.userId,
            clientOwnerEmail: selectedClientOwnerUserRecord.userEmail,
        }
        const authToken = await getAuthToken();

        API.post("AnnualCompensations",
            "/billing-data",
            {
                headers: {
                    'Token': authToken
                },
                body: requestBody
            }).then(response => {
                setDownloading(false);
                handleDownloadData();
                handleClose();
            }).catch(error => {
                console.log(error);
                handleError("An error occurred downloading data")
            })

    }

    const handleChangeBillableType = (event) => {
        setBillableType(event.target.value);
        setIsBillableTypeSelected(true);
        setClientNameInvalid(false);
        setRequiredNonBillable(false);
        setClientName("");
        setSelectedClientOwnerUserRecord({});
        setNsId("");
        setIsSubscriber(false);
    }

    const handleNotesEntry = (event) => {
        setUserNotes(event.target.value);
        const limit = 250 - event.target.value.length
        setCharacterLimit(limit);
        setRequiredNonBillable(false);
    }

    const handleSettingClientMetadata = (clientMetadata) => {
        setClientName(clientMetadata?.data?.companyName);
        setNsId(clientMetadata?.data?.nsId);
        handleSetClientOwner(clientMetadata);
        setIsSubscriber(clientMetadata?.data?.subscriberClient);
    }

    const handleSetClientOwner = (clientMetadata) => {
        if (clientMetadata?.data?.clientOwner && clientMetadata?.data?.clientOwnerId && clientMetadata?.data?.clientOwnerEmail) {
            setClientOwnerNameLookupTerm(clientMetadata?.data?.clientOwner);
            const clientOwnerData = {
                userName: clientMetadata?.data?.clientOwner,
                userId: clientMetadata?.data?.clientOwnerId,
                userEmail: clientMetadata?.data?.clientOwnerEmail
            };
            setSelectedClientOwnerUserRecord(clientOwnerData);
        } else {
            setClientOwnerNameLookupTerm("");
            setSelectedClientOwnerUserRecord({});
        }
    };

    const handleClose = () => {
        setBillableType('');
        setIsBillableTypeSelected(true);
        setRequiredNonBillable(false);

        setUserNotes('');
        setCharacterLimit(250);

        setShowError(false);
        setErrorMessage('');

        setClientNameInvalid(false);
        setClientName("");

        setSelectedClientOwnerUserRecord({});
        setClientOwnerNameLookupTerm("");
        setClientOwnerNameValid(true);
        setClientOwnerNameErrorMessage("");
        setIsSubscriber(false);
        setNsId("");

        onClose();
    };

    return (
        <Modal show={show} onHide={handleClose} size="md" contentClassName="modal-container">
            <Modal.Header closeButton>
                <h5 className='mb-0'>Data Billing</h5>
            </Modal.Header>
            <Form
                noValidate
                id="clientNameforDataExportForm"
                onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); handleSubmit(event); }}>
                <Modal.Body>
                    <Form.Group className="mb-2">
                        <Form.Label>Billable Type</Form.Label>
                        <Form.Select onChange={(event) => handleChangeBillableType(event)} isInvalid={!isBillableTypeSelected}>
                            <option value="">Select a value</option>
                            <option value={BILLABLE_TYPES.billable}>{HUMAN_READABLE_BILLABLE_TYPES[BILLABLE_TYPES.billable]}</option>
                            <option value={BILLABLE_TYPES.customBillable}>{HUMAN_READABLE_BILLABLE_TYPES[BILLABLE_TYPES.customBillable]}</option>
                            <option value={BILLABLE_TYPES.nonBillableRFP}>{HUMAN_READABLE_BILLABLE_TYPES[BILLABLE_TYPES.nonBillableRFP]}</option>
                            <option value={BILLABLE_TYPES.nonBillableOther}>{HUMAN_READABLE_BILLABLE_TYPES[BILLABLE_TYPES.nonBillableOther]}</option>
                            <option value={BILLABLE_TYPES.clientDownload}>{HUMAN_READABLE_BILLABLE_TYPES[BILLABLE_TYPES.clientDownload]}</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Control.Feedback type="invalid">Please select a billable type</Form.Control.Feedback>
                        {(billableType === BILLABLE_TYPES.billable || billableType === BILLABLE_TYPES.customBillable) ?
                            <>
                                <ClientMetadataLookupInput
                                    value={clientName}
                                    handleValueChange={setClientName}
                                    handleClientMetadataRecordSelection={handleSettingClientMetadata}
                                    noValidate={false}
                                    inputLabel="Client Name"
                                    placeholder="Please enter client name"
                                    isValueValid={!clientNameInvalid}
                                    customErrorMessage="Please enter client name to bill"
                                />
                                <UserLookupInput
                                    value={clientOwnerNameLookupTerm}
                                    handleValueChange={setClientOwnerNameLookupTerm}
                                    handleUserRecordSelection={setSelectedClientOwnerUserRecord}
                                    placeholder='Please enter client owner name'
                                    inputLabel="FWC Client Owner Name"
                                    noValidate={false}
                                    isValueValid={clientOwnerNameValid}
                                    customErrorMessage={clientOwnerNameErrorMessage}
                                />
                            </> : ""}
                    </Form.Group>
                    <Form.Group className="mb-4" controlId="clientNameforDataExportForm.userNotesInput">
                        <Form.Label>User Notes</Form.Label>
                        <Form.Control
                            placeholder="Please enter any notes for this download"
                            onChange={(event) => handleNotesEntry(event)}
                            as="textarea"
                            maxLength="250"
                            isInvalid={requiredNonBillable}
                        />
                        <span className='character-limit-count'>Character limit: {characterLimit}</span>
                        <Form.Control.Feedback type="invalid">
                            User Notes required for non-billable downloads
                        </Form.Control.Feedback>
                    </Form.Group>
                    {showError ? <div className="alert alert-danger" role="alert">{errorMessage}</div> : null}
                </Modal.Body>
                <Modal.Footer>
                    {downloading ?
                        <Button variant="btn btn-primary"
                            disabled={true}>
                            <output>Downloading...</output>
                            <Spinner
                                as="span"
                                animation="grow"
                                size="sm"
                                aria-hidden="true"
                            />
                        </Button> :
                        <Button variant="btn btn-primary"
                            type="submit"
                            style={{ "width": "25%" }}>
                            Download
                        </Button>}
                    <Button variant="btn btn-secondary" onClick={handleClose} style={{ "width": "20%" }} disabled={downloading}>
                        Close
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal >
    );
}

DashboardDownloadConsole.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func,
    compensationIds: PropTypes.instanceOf(Set),
    selectedTemplate: PropTypes.object
};

export default DashboardDownloadConsole;